import React, { useState, lazy, Suspense } from 'react';
import { ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';
// SchemaMarkup removed - JSON-LD now managed in public/index.html to avoid duplicates
import Hero from '../components/Hero';
import GradientSection from '../components/GradientSection';
import { REVIEWS } from '../constants/reviews';

// Lazy load below-fold sections (Stats, Tours, Testimonials all import framer-motion ~32KB gzipped)
const Stats = lazy(() => import('../components/Stats'));
const Tours = lazy(() => import('../components/Tours'));
const Testimonials = lazy(() => import('../components/Testimonials'));

const homeFAQs = [
  { question: 'Are horse carriage rides in Central Park safe?', answer: 'Yes, NYC horse carriage rides are among the most regulated in the world. All horses receive regular veterinary checkups, work limited hours, and rest during extreme weather. NYC Royal Carriage has maintained an excellent safety record since 1999 with a 5.0★ rating on Google.' },
  { question: 'What is the best time for a Central Park carriage ride?', answer: 'Every season offers a unique experience. Spring (April-May) has cherry blossoms, summer evenings offer golden sunsets, fall (October-November) brings stunning foliage, and winter features a magical snowy landscape. Weekend sunsets and evening rides are the most popular for couples.' },
  { question: 'Do I need to book a horse carriage ride in advance?', answer: 'Yes. Reservations are required — we do not take walk-up bookings at the carriage stand. Book online at nycroyalcarriage.com/book or call (646) 656-9717 to reserve your date and time; weekends, holidays and proposals fill up first, so book early.' },
  { question: 'What landmarks will I see on a Central Park carriage ride?', answer: 'Depending on your tour length, you may see Bethesda Fountain, Bow Bridge, Strawberry Fields (John Lennon memorial), The Mall, Cherry Hill, the Literary Walk, and more. Our drivers provide narrated commentary about each landmark\'s history and significance.' },
  { question: 'Can I bring children on a horse carriage ride?', answer: 'Absolutely! Children love our carriage rides and are welcome on all tours. Each carriage seats up to 4 adults; with children (12 and under) the seating can be 3 adults & up to 2 children, 2 adults & up to 4 children, or 1 adult & up to 6 children. We offer family-friendly narration and our horses are gentle and accustomed to young riders.' },
  { question: 'How are NYC Royal Carriage\'s horses cared for?', answer: 'Our 15 horses are the heart of what we do. Each receives daily grooming, premium nutrition, veterinary care three times a year, farrier hoof care, and 6–12 weeks of annual vacation on green pastures outside the city. Born and raised in Lancaster County, Pennsylvania and trained with gentle, positive-reinforcement methods, they work strictly limited hours with mandated rest under NYC Department of Health rules. We believe healthy, well-rested horses are both our responsibility and the reason our guests enjoy such a calm, comfortable ride.' },
  { question: 'What makes NYC Royal Carriage different from other carriage services?', answer: 'We are a family-owned business operating in Central Park since 1999 — the direct operator, not a third-party reseller — rated 5.0★ on Google from 175 verified reviews. Every booking is a private carriage for up to 4 guests with transparent per-carriage pricing and no hidden fees. Our licensed, multilingual drivers (English, Spanish, Turkish, French) narrate the park\'s history and famous film locations, and we are fully insured, our carriages are inspected annually, and our horses are permitted and inspected by the NYC Department of Health and Mental Hygiene. We specialize in proposals, anniversaries, and family milestones.' },
];

// FAQ schema removed from homepage to avoid fragmentation with /faq/ page
// The /faq/ page is the canonical source of FAQ schema for Google

const HomePage = () => {
  const [openFAQ, setOpenFAQ] = useState({});

  return (
    <div className="bg-white">
      <SEO
        title="NYC Horse Carriage Rides | Central Park Tours from $105"
        description="Book NYC horse carriage rides in Central Park. 5.0★ on Google. Tours from $105 for up to 4 guests. No hidden fees. Proposals, anniversaries, family tours since 1999."
        keywords="NYC horse carriage rides, nyc carriage rides, Central Park carriage tour, horse carriage ride New York, central park carriage ride cost, how much is a carriage ride in central park, romantic carriage ride NYC, proposal carriage ride NYC"
        canonical="/"
      />
      {/* FAQ schema moved to /faq/ page only to avoid fragmentation */}

      {/* Hero Section - Dark Background */}
      <Hero />

      {/* Below-fold sections lazy loaded to reduce initial bundle (defers framer-motion) */}
      <Suspense fallback={<div style={{ minHeight: '200px' }} />}>
        <GradientSection variant="bronze-to-white" className="py-16">
          <Stats />
        </GradientSection>
      </Suspense>

      <Suspense fallback={<div style={{ minHeight: '600px' }} />}>
        <GradientSection variant="white-to-gold" className="py-20 cv-auto">
          <Tours />
        </GradientSection>
      </Suspense>

      <Suspense fallback={<div style={{ minHeight: '400px' }} />}>
        <GradientSection variant="gold-to-white" className="py-20 cv-auto">
          <Testimonials />
        </GradientSection>
      </Suspense>

      {/* Where we are reviewed and bookable. Three platforms, equal weight,
          hairline-divided — no borders, no chips. Only Google carries a figure
          because Google is the only count we can verify (src/constants/reviews.js);
          the other two are named and linked without a claim attached. The former
          "Top Rated" / "Featured" / "Reviews" labels were invented and were
          removed 2026-09-08, together with the unclaimed TripAdvisor listing. */}
      <section className="py-16 bg-white cv-auto border-t border-neutral-100">
        <div className="container-custom">
          <p className="text-center text-neutral-600 font-body text-[11px] uppercase tracking-[0.2em] mb-10">
            Reviewed &amp; bookable on
          </p>
          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 sm:divide-x divide-neutral-200">
            {[
              {
                name: 'Google',
                detail: `${REVIEWS.google.rating.toFixed(1)}★ · ${REVIEWS.google.count} reviews`,
                href: 'https://maps.app.goo.gl/C1G3Ep5TErv41Qn9A',
              },
              {
                name: 'GetYourGuide',
                detail: 'View our listing',
                href: 'https://www.getyourguide.com/nyc-royal-carriage-rides-s721366/',
              },
              {
                name: 'Viator',
                detail: 'View our listing',
                href: 'https://www.viator.com/tours/New-York-City/Classic-NYC-Movie-Style-Horse-Carriage-Tour/d687-5625876P4',
              },
            ].map(({ name, detail, href }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center gap-1.5 px-4 py-4 sm:py-2 transition-colors duration-200"
              >
                <span className="font-display text-xl md:text-2xl text-neutral-900 group-hover:text-brand-700 transition-colors">
                  {name}
                </span>
                <span className="font-body text-xs text-neutral-600 group-hover:text-neutral-700 transition-colors">
                  {detail}
                </span>
              </a>
            ))}
          </div>
          <div className="text-center mt-10">
            <a
              href="/reviews/"
              className="font-body text-xs uppercase tracking-[0.14em] text-brand-700 hover:text-brand-800 border-b border-brand-300 hover:border-brand-700 pb-1 transition-colors"
            >
              Read our reviews
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-neutral-50 cv-auto">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-neutral-900 mb-10 text-center">What Do Visitors Ask About Central Park Carriage Rides?</h2>
            <div className="space-y-3">
              {homeFAQs.map((faq, index) => (
                <div key={index} className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
                  <button
                    onClick={() => setOpenFAQ(prev => ({ ...prev, [index]: !prev[index] }))}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-neutral-50 transition-colors"
                  >
                    <span className="font-body font-semibold text-neutral-900 pr-4">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-neutral-400 flex-shrink-0 transition-transform duration-200 ${openFAQ[index] ? 'rotate-180' : ''}`} />
                  </button>
                  {openFAQ[index] && (
                    <div className="px-6 pb-5">
                      <p className="text-neutral-600 font-body leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="/faq/" className="text-brand-700 hover:text-brand-800 font-body font-semibold text-sm transition-colors">
                View All Frequently Asked Questions &rarr;
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 